import { X } from "lucide-react";
import { useState } from "react";

import type { ProjectCreatePayload } from "@/lib/api";

interface Props {
  onClose: () => void;
  onSubmit: (payload: ProjectCreatePayload) => void;
  submitting: boolean;
  error?: string;
}

export function CreateProjectDialog({ onClose, onSubmit, submitting, error }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [s3Bucket, setS3Bucket] = useState("audiolabeling-inputs");
  const [s3Prefix, setS3Prefix] = useState("");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-bg-surface p-5 shadow-xl md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">New project</h2>
            <p className="text-sm text-text-muted">
              Each project is bound to one S3 prefix. Audio files there become tagging tasks.
            </p>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text" title="Close">
            <X size={20} />
          </button>
        </div>

        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({ name, description: description || null, s3_bucket: s3Bucket, s3_prefix: s3Prefix });
          }}
        >
          <Field label="Name" required>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Description">
            <textarea
              className="input min-h-[60px] resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="S3 bucket" required>
              <input
                className="input font-mono text-xs"
                value={s3Bucket}
                onChange={(e) => setS3Bucket(e.target.value)}
                required
              />
            </Field>
            <Field label="S3 prefix">
              <input
                className="input font-mono text-xs"
                placeholder="nasa1/audio/"
                value={s3Prefix}
                onChange={(e) => setS3Prefix(e.target.value)}
              />
            </Field>
          </div>
          <p className="text-xs text-text-dim">
            Tag schema defaults to <span className="text-text">Drone</span> +{" "}
            <span className="text-text">Not Sure</span>. Edit in project settings.
          </p>

          {error && (
            <div className="rounded-md border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-text-muted">
        {label} {required && <span className="text-status-error">*</span>}
      </label>
      {children}
    </div>
  );
}
